import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Space, Steps, Typography, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import {
  PRICE_IMPORT_STATUS,
  buildPriceImportPlan,
  parsePriceImportFile,
  summarizePriceImportPlan,
} from "functions/priceImport";
import {
  applyPriceImportLines,
  createPriceImportRun,
  fetchPriceImportRuns,
  markPriceImportRunFailed,
  rollbackPriceImportRun,
} from "functions/firestore/priceImport";
import UploadStep from "./components/UploadStep";
import ReviewStep from "./components/ReviewStep";
import ResultStep from "./components/ResultStep";
import ApplyProgress from "./components/ApplyProgress";
import HistoryCard from "./components/HistoryCard";

const { Paragraph, Title } = Typography;

const STEP = { UPLOAD: 0, REVIEW: 1, RESULT: 2 };

/**
 * Bulk bijwerken van verkoopprijzen via Excel, in drie stappen: bestand kiezen, alles controleren,
 * en pas daarna doorvoeren. Het doorvoeren zelf gebeurt in transacties (zie
 * functions/firestore/priceImport.js) en wordt vastgelegd zodat het terug te draaien is.
 */
const PriceImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const email = useSelector((state) => state.firebaseReducer.auth.email);

  const [step, setStep] = useState(STEP.UPLOAD);
  const [isReading, setIsReading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [fileMeta, setFileMeta] = useState({ fileName: "", sheetName: "" });
  const [lines, setLines] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const [isApplying, setIsApplying] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [appliedLines, setAppliedLines] = useState([]);
  const [runId, setRunId] = useState(null);
  const [runLogFailed, setRunLogFailed] = useState(false);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [rollbackRunId, setRollbackRunId] = useState(null);

  const summary = useMemo(() => summarizePriceImportPlan(lines, products), [lines, products]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      setRuns(await fetchPriceImportRuns(10));
    } catch (error) {
      // De geschiedenis is een extraatje; als het niet lukt blokkeert dat de import niet.
      console.error("Kon prijsimports niet laden:", error);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleFileSelected = async (file) => {
    if (!products || products.length === 0) {
      setFileError(t("price_import.upload.errors.no_products"));
      return;
    }

    setIsReading(true);
    setFileError(null);

    const parsed = await parsePriceImportFile(file);

    if (parsed.error) {
      setFileError(t(`price_import.upload.errors.${parsed.error}`));
      setIsReading(false);
      return;
    }

    const plan = buildPriceImportPlan(parsed.rows, products);
    setLines(plan);
    // Standaard staan alle daadwerkelijke wijzigingen aangevinkt; fouten kunnen niet mee.
    setSelectedKeys(plan.filter((line) => line.status === PRICE_IMPORT_STATUS.CHANGE).map((line) => line.key));
    setFileMeta({ fileName: file.name, sheetName: parsed.sheetName });
    setIsReading(false);
    setStep(STEP.REVIEW);
  };

  const handleRestart = () => {
    setStep(STEP.UPLOAD);
    setLines([]);
    setSelectedKeys([]);
    setFileError(null);
    setFileMeta({ fileName: "", sheetName: "" });
    setResult(null);
    setAppliedLines([]);
    setRunId(null);
    setRunLogFailed(false);
    setProgress({ processed: 0, total: 0 });
    loadRuns();
  };

  const handleApply = async (selectedLines) => {
    if (selectedLines.length === 0) return;

    setIsApplying(true);
    setStep(STEP.RESULT);
    setAppliedLines(selectedLines);
    setProgress({ processed: 0, total: selectedLines.length });

    // Eerst vastleggen wat we gaan doen (inclusief de oude prijzen), dan pas schrijven.
    const run = await createPriceImportRun({
      lines: selectedLines,
      email,
      fileName: fileMeta.fileName,
      summary: {
        totalRows: summary.totalRows,
        changeCount: summary.changeCount,
        unchangedCount: summary.unchangedCount,
        errorCount: summary.errorCount,
        selectedCount: selectedLines.length,
      },
    });

    setRunId(run.id);
    setRunLogFailed(!run.id);

    try {
      const applyResult = await applyPriceImportLines({
        lines: selectedLines,
        email,
        runId: run.id,
        onProgress: ({ processed, total }) => setProgress({ processed, total }),
      });

      // Geen toast: het resultaatscherm toont de uitkomst al.
      setResult({ ...applyResult, failed: false });
    } catch (error) {
      console.error("Prijsimport mislukt:", error);
      const partial = error.partial || { applied: [], conflicts: [], missing: [] };
      setResult({ ...partial, failed: true, error: error.message });
      await markPriceImportRunFailed(run.id, error);
    } finally {
      setIsApplying(false);
      loadRuns();
    }
  };

  const performRollback = async (run) => {
    setRollbackRunId(run.id);
    try {
      const rollbackResult = await rollbackPriceImportRun({ run, email });
      const issues = rollbackResult.conflicts.length + rollbackResult.missing.length;

      if (rollbackResult.applied.length === 0 && issues === 0) {
        messageApi.info(t("price_import.result.rollback_nothing"));
      } else if (issues > 0) {
        messageApi.warning(
          t("price_import.result.rollback_issues", { count: rollbackResult.applied.length, issues })
        );
      } else {
        messageApi.success(t("price_import.result.rollback_done", { count: rollbackResult.applied.length }));
      }

      // Na terugdraaien is er niets meer om nog eens terug te draaien.
      if (run.id === runId) setRunId(null);
    } catch (error) {
      console.error("Terugdraaien mislukt:", error);
      messageApi.error(t("price_import.result.rollback_failed", { message: error.message }));
    } finally {
      setRollbackRunId(null);
      loadRuns();
    }
  };

  const handleResultRollback = async () => {
    const applied = result?.applied || [];
    if (!runId || applied.length === 0) {
      messageApi.info(t("price_import.result.rollback_nothing"));
      return;
    }
    await performRollback({ id: runId, lines: applied });
  };

  return (
    <>
      {contextHolder}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("price_import.title")}
            </Title>
            {step === STEP.UPLOAD && (
              <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 720 }}>
                {t("price_import.subtitle")}
              </Paragraph>
            )}
          </div>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.productList)}>
              {t("products")}
            </Button>
          </Space>
        </div>

        <Steps
          style={{ marginTop: 24 }}
          current={step}
          items={[
            { title: t("price_import.steps.upload") },
            { title: t("price_import.steps.review") },
            { title: t("price_import.steps.result") },
          ]}
        />
      </Card>

      <div style={{ marginTop: 16 }}>
        {step === STEP.UPLOAD && (
          <>
            {!products ? (
              <Card>
                <Loading />
              </Card>
            ) : (
              <UploadStep
                products={products}
                isReading={isReading}
                fileError={fileError}
                onFileSelected={handleFileSelected}
              />
            )}
            <HistoryCard
              runs={runs}
              loading={runsLoading}
              rollbackRunId={rollbackRunId}
              onRollback={performRollback}
            />
          </>
        )}

        {step === STEP.REVIEW && (
          <ReviewStep
            lines={lines}
            summary={summary}
            fileName={fileMeta.fileName}
            sheetName={fileMeta.sheetName}
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            onApply={handleApply}
            onRestart={handleRestart}
          />
        )}

        {step === STEP.RESULT &&
          (isApplying || !result ? (
            <ApplyProgress processed={progress.processed} total={progress.total} />
          ) : (
            <ResultStep
              result={result}
              appliedLines={appliedLines}
              runId={runId}
              runLogFailed={runLogFailed}
              rollbackLoading={rollbackRunId === runId}
              onRollback={handleResultRollback}
              onRestart={handleRestart}
              onBackToProducts={() => navigate(urls.productList)}
            />
          ))}
      </div>
    </>
  );
};

export default PriceImport;
